import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import { homeNode } from "motw_core/Repositories/WorkflowRepository";
import * as React from "react";
import { LoadingSpinner } from "source/app/presentation/MainPanel/Calendar/LoadingSpinner";
import { WithAnimatedProgressBarAsync } from "source/app/presentation/Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import { SwalButtons } from "source/app/services/Shared/RenderSwal/SwalBody/SwalButtons";
import { AreYouSure } from "source/app/services/Shared/RenderSwal/RenderSwal";
import { UpdateNodeAsync } from "source/app/services/Workflows/ChangeData/UpdateNodeAsync";
import { SetRootNodeAsync } from "source/app/services/Workflows/SetRootNode";
import * as toastr from "toastr";
import { GetMoveConfirmationText } from "./GetMoveConfirmationText";
import { MoveMode } from "./MoveMode";
import { MoveModeSelector } from "./MoveModeSelector";
import * as styles from "./MoveNode.css";
import { MoveAllChildrenAsync } from "./MoveWorkflowNodesAsync";
import { useMoveNodeSearch } from "./useMoveNodeSearch";

export function MoveNode({
    rootNode,
    closeEditor,
}: {
    rootNode: IWorkflowNode;
    closeEditor: () => void;
}) {
    const [moveMode, setMoveMode] = React.useState(MoveMode.NodeAndChildren);
    const search = useMoveNodeSearch(rootNode);
    const moveIsInvalid =
        !search.selectedParent ||
        (moveMode === MoveMode.NodeAndChildren &&
            search.selectedParent.id === rootNode.parentid) ||
        (moveMode !== MoveMode.NodeAndChildren &&
            search.descendantIds.length === 0);

    if (rootNode.id === homeNode.id) {
        return null;
    }

    const moveToSelectedParent = () => {
        if (!search.selectedParent) {
            return;
        }

        const destination = search.selectedParent;
        AreYouSure(
            GetMoveConfirmationText(
                rootNode,
                destination,
                moveMode,
                search.descendantIds.length
            ),
            async () => {
                await WithAnimatedProgressBarAsync(async () => {
                    if (moveMode === MoveMode.NodeAndChildren) {
                        await UpdateNodeAsync(rootNode, {
                            parentid: destination.id,
                        });
                        return;
                    }

                    await MoveAllChildrenAsync(
                        rootNode,
                        destination,
                        search.descendantIds,
                        moveMode === MoveMode.AllChildrenAndDelete
                    );

                    if (moveMode === MoveMode.AllChildrenAndDelete) {
                        closeEditor();
                        await NavigateAfterEditorCloses(destination.id);
                    }
                });

                toastr.success(GetSuccessText(rootNode, destination, moveMode));
                search.setSearchText("");
                search.setSelectedParent(null);
            },
            `70px`
        );
    };

    return (
        <section className={styles.container}>
            <label className={styles.label} htmlFor="moveNodeParentSearch">
                Search for a new parent
            </label>
            <div className={styles.searchRow}>
                <input
                    id="moveNodeParentSearch"
                    type="search"
                    value={search.searchText}
                    placeholder="Type a workflow node name or ID..."
                    autoComplete="off"
                    disabled={!search.canSearch}
                    onChange={(event) => {
                        search.setSearchText(event.target.value);
                        search.setSelectedParent(null);
                    }}
                    className={styles.searchInput}
                />
            </div>
            {search.isSearching ? (
                <LoadingSpinner className={styles.spinner} />
            ) : null}
            {search.results.length > 0 ? (
                <div className={styles.results}>
                    {search.results.map((node) => (
                        <button
                            type="button"
                            key={node.id}
                            className={styles.result}
                            onClick={() => {
                                if (node.id === rootNode.parentid) {
                                    setMoveMode(MoveMode.AllChildren);
                                }
                                search.setSelectedParent(node);
                                search.setSearchText(node.name);
                                search.setResults([]);
                            }}
                        >
                            <span>{node.name}</span>
                            <small>#{node.id}</small>
                        </button>
                    ))}
                </div>
            ) : null}
            {search.selectedParent ? (
                <>
                    <div className={styles.selection}>
                        Destination:
                        <strong>
                            {search.selectedParent.name} (#
                            {search.selectedParent.id})
                        </strong>
                    </div>
                    <MoveModeSelector
                        descendantCount={search.descendantIds.length}
                        nodeMoveDisabled={
                            search.selectedParent.id === rootNode.parentid
                        }
                        moveMode={moveMode}
                        setMoveMode={setMoveMode}
                    />
                    <div className={styles.moveAction}>
                        <SwalButtons.Save
                            label="Move"
                            disabled={moveIsInvalid}
                            onClick={moveToSelectedParent}
                        />
                    </div>
                </>
            ) : null}
        </section>
    );
}

async function NavigateAfterEditorCloses(destinationId: number) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
    await SetRootNodeAsync(destinationId);
}

function GetSuccessText(
    rootNode: IWorkflowNode,
    destination: IWorkflowNode,
    moveMode: MoveMode
) {
    if (moveMode === MoveMode.AllChildrenAndDelete) {
        return `"${rootNode.name}" was dissolved into "${destination.name}"`;
    }

    if (moveMode === MoveMode.AllChildren) {
        return `All descendants moved to "${destination.name}"`;
    }

    return `"${rootNode.name}" was moved to "${destination.name}"`;
}
